/**
 * Contract scope register (operator order 2026-08-29): what the documents say
 * is in scope, what they say is out of scope, and what they do not address.
 * Every statement shows its verbatim quote and page; compliance confirms or
 * rejects each one; superseded readings stay visible as history.
 */
import React, { useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

export const STATE_CFG = {
  in_scope: { label: 'In scope', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  out_of_scope: { label: 'Out of scope', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  not_addressed: { label: 'Not addressed', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
export const CATEGORY_LABEL = {
  function: 'Delegated function', service: 'Service or product', line_of_business: 'Line of business',
  population: 'Population', geography: 'Service area', codes: 'Codes', carve_out: 'Carve-out or exclusion',
  sub_delegation: 'Subdelegation', data_and_privacy: 'Data and privacy', reporting: 'Reports the vendor must deliver', other: 'Other',
};
const ROLE_LABEL = { contract: 'Contract', baa: 'BAA', amendment: 'Amendment', addendum: 'Addendum', exhibit: 'Exhibit' };
// A DATE column arrives as 'YYYY-MM-DD…'; read it as a calendar day, never as UTC midnight localized (the day-early bug).
const fmt = (d) => {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export function StatePill({ state }) {
  const c = STATE_CFG[state] || STATE_CFG.not_addressed;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${c.cls}`}>{c.label}</span>;
}

export function ScopeQuote({ item, docsById }) {
  const doc = item.document_id ? docsById[item.document_id] : null;
  const [showRef, setShowRef] = useState(false);
  return (
    <div className="space-y-2">
      {item.quote && (
        <blockquote className="text-[12.5px] text-slate-700 bg-amber-50/60 border-l-4 border-amber-300 rounded-r-lg px-3 py-2 whitespace-pre-wrap break-words">
          “{item.quote}”
          <div className="mt-1 text-[11px] text-slate-500 not-italic">
            {doc ? `${ROLE_LABEL[doc.doc_role] || 'Document'} · ${doc.file_name}` : 'Document'}{item.page ? ` · page ${item.page}` : ''}
          </div>
        </blockquote>
      )}
      {Array.isArray(item.codes) && item.codes.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center text-[11px] text-slate-600">
          <span className="font-semibold">Codes listed in the text:</span>
          {item.codes.map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">{c}</span>)}
        </div>
      )}
      {item.codes_by_reference && (item.reference_page ? (
        <div className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5">
          <div className="flex items-center justify-between gap-3">
            <span>Detailed in <span className="font-semibold">{item.codes_by_reference}</span>, which is in this document (page {item.reference_page || '—'}).</span>
            {item.reference_excerpt && (
              <button type="button" onClick={() => setShowRef((v) => !v)} className="font-semibold underline whitespace-nowrap">
                {showRef ? 'Hide what it says' : 'Show what it says'}
              </button>
            )}
          </div>
          {showRef && item.reference_excerpt && (
            <pre className="mt-2 max-h-[420px] overflow-auto whitespace-pre-wrap break-words font-sans text-[11.5px] text-slate-800 bg-white border border-sky-100 rounded-lg px-3 py-2">{item.reference_excerpt}</pre>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          Defined by reference to <span className="font-semibold">{item.codes_by_reference}</span>, which is not in the documents on file. Upload that attachment to read it in.
        </div>
      ))}
    </div>
  );
}

function ScopeRow({ item, docsById, itemsById, canConfirm, onAction, busyId }) {
  const [open, setOpen] = useState(false);
  const history = item.status !== 'active';
  const superseded = item.superseded_by ? itemsById[item.superseded_by] : null;
  const supersedes = item.supersedes ? itemsById[item.supersedes] : null;
  const confirmed = !!item.confirmed_at;
  const rejected = item.status === 'rejected';
  const cat = CATEGORY_LABEL[item.category] || item.category;
  return (
    <>
      <tr className={`border-t border-slate-100 ${history ? 'opacity-60' : ''} ${open ? 'bg-slate-50/60' : 'hover:bg-slate-50/40'}`}>
        <td className="px-3 py-2.5 align-top cursor-pointer" onClick={() => setOpen((o) => !o)}>
          <div className="text-[13px] font-semibold text-slate-800">{item.label}</div>
          {item.detail && <div className="text-[12px] text-slate-500 mt-0.5">{item.detail}</div>}
          <div className="text-[10.5px] uppercase tracking-wide text-slate-400 mt-1">{cat}</div>
        </td>
        <td className="px-3 py-2.5 align-top whitespace-nowrap"><StatePill state={item.state} /></td>
        <td className="px-3 py-2.5 align-top text-[12px] text-slate-600 whitespace-nowrap">
          {item.document_id && docsById[item.document_id]
            ? <>{ROLE_LABEL[docsById[item.document_id].doc_role] || 'Document'}{item.page ? <span className="text-slate-400"> · p. {item.page}</span> : null}</>
            : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-3 py-2.5 align-top text-[12px] whitespace-nowrap">
          {item.state === 'not_addressed' ? (
            <span className="text-slate-400">Nothing in the documents</span>
          ) : history ? (
            <span className="text-slate-500">{item.status === 'superseded' ? `Superseded${superseded?.effective_from ? ` from ${fmt(superseded.effective_from)}` : ''}` : item.status === 'rejected' ? 'Disputed' : 'Retired'}</span>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-emerald-700 font-semibold" title="Copied character for character from the document">Verbatim{confirmed ? ` · noted by ${item.confirmed_by_name || 'compliance'}` : ''}</span>
              {canConfirm && (
                <button type="button" disabled={busyId === item.id} onClick={() => onAction(item, 'reject')}
                  className="text-[11px] text-slate-400 hover:text-slate-700 underline disabled:opacity-50" title="The words are on the page but the reading (state or category) is wrong: take it out of the register">Dispute</button>
              )}
            </div>
          )}
          {history && canConfirm && (
            <button type="button" onClick={() => onAction(item, 'reopen')} className="ml-2 text-[11px] text-slate-500 underline"
              title="Bring this reading back as active">Restore</button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60">
          <td colSpan={4} className="px-4 pb-3 pt-1">
            <ScopeQuote item={item} docsById={docsById} />
            {supersedes && (
              <div className="mt-2 text-[11.5px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5">
                Replaces the earlier reading “{supersedes.label}” ({STATE_CFG[supersedes.state]?.label}{supersedes.document_id && docsById[supersedes.document_id] ? `, ${ROLE_LABEL[docsById[supersedes.document_id].doc_role] || 'document'}${supersedes.page ? ` p. ${supersedes.page}` : ''}` : ''}){item.effective_from ? `, effective ${fmt(item.effective_from)}` : ''}.
              </div>
            )}
            {superseded && (
              <div className="mt-2 text-[11.5px] text-slate-600 bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1.5">
                Superseded by “{superseded.label}” ({STATE_CFG[superseded.state]?.label}){superseded.effective_from ? ` effective ${fmt(superseded.effective_from)}` : ''}. Kept here as history.
              </div>
            )}
            {item.review_note && <div className="mt-2 text-[11.5px] text-slate-600">Note: {item.review_note}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ScopeTab({ contractId, scope, reload, canConfirm = true, onGoDocuments }) {
  const [filter, setFilter] = useState('active');   // active | in_scope | out_of_scope | not_addressed | history
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const items = scope?.items || [];
  const docs = scope?.documents || [];
  const runs = scope?.runs || [];
  const docsById = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, d])), [docs]);
  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const reading = runs.filter((r) => ['queued', 'reading'].includes(r.status));
  const counts = scope?.counts || {};

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === 'history') { if (i.status === 'active') return false; }
      else if (i.status !== 'active') return false;
      if (filter === 'in_scope' || filter === 'out_of_scope' || filter === 'not_addressed') { if (i.state !== filter) return false; }
      if (category && i.category !== category) return false;
      if (needle) {
        const hay = `${i.label} ${i.detail || ''} ${i.quote || ''} ${(i.codes || []).join(' ')} ${i.codes_by_reference || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    }).sort((a, b) => {
      const order = { in_scope: 0, out_of_scope: 1, not_addressed: 2 };
      if (order[a.state] !== order[b.state]) return order[a.state] - order[b.state];
      if ((a.category || '') !== (b.category || '')) return (a.category || '').localeCompare(b.category || '');
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [items, filter, category, q]);

  const act = async (item, action) => {
    setBusyId(item.id); setErr('');
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/scope/items/${item.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Could not save');
      await reload();
    } catch (e) { setErr(e.message); }
    setBusyId(null);
  };

  const pills = [
    ['active', 'All', (counts.in_scope || 0) + (counts.out_of_scope || 0) + (counts.not_addressed || 0)],
    ['in_scope', 'In scope', counts.in_scope || 0],
    ['out_of_scope', 'Out of scope', counts.out_of_scope || 0],
    ['not_addressed', 'Not addressed', counts.not_addressed || 0],
    ['history', 'History', counts.history || 0],
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {pills.map(([id, label, n]) => (
          <button key={id} type="button" onClick={() => setFilter(id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {label} <span className={filter === id ? 'text-orange-100' : 'text-slate-400'}>{n}</span>
          </button>
        ))}
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
          <option value="">Every category</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search statements, quotes, codes…"
          className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 text-slate-700 min-w-[240px]" />
        <span className="text-[11.5px] text-slate-400 ml-auto">{(counts.in_scope || 0) + (counts.out_of_scope || 0)} statements, every one verbatim</span>
      </div>

      {reading.length > 0 && (
        <div className="bg-slate-50 rounded-xl px-4 py-3">
          {reading.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs text-slate-600">
              <span>Reading {docsById[r.document_id]?.file_name || 'document'} · {r.progress_msg || 'queued'}</span>
              <span>{r.progress_pct || 0}%</span>
            </div>
          ))}
        </div>
      )}
      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}

      {!items.length && !reading.length && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
          Nothing has been read into the register yet. {docs.length ? 'Open Documents and read the contract in.' : 'Upload the contract under Documents; it is read in as soon as its text is on file.'}
          {onGoDocuments && <div className="mt-2"><button type="button" onClick={onGoDocuments} className="text-orange-700 font-semibold text-xs">Go to Documents →</button></div>}
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2 font-semibold">Statement</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Where</th>
                <th className="px-3 py-2 font-semibold">Source</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((it) => (
                <ScopeRow key={it.id} item={it} docsById={docsById} itemsById={itemsById} canConfirm={canConfirm} onAction={act} busyId={busyId} />
              ))}
              {!shown.length && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-slate-400">Nothing matches this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-400">
        Every statement is a verbatim passage of the document with its page; nothing is paraphrased or inferred. Silence is shown as “Not addressed”, never as a conclusion. Codes appear only when the text lists them. An attachment inside the document is read and shown in full under the line that points to it. Dispute a line only when its reading (in or out of scope, category) is wrong.
      </p>
    </div>
  );
}
